import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';

export interface LeadershipWebSocketData {
  type: string;
  data: any;
  timestamp: string;
  userId?: string;
  sprintId?: string;
  projectId?: string;
}

export interface LeadershipWebSocketError {
  type: string;
  message: string;
  timestamp: string;
  userId?: string;
  sprintId?: string;
  projectId?: string;
}

export const useLeadershipWebSocket = () => {
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  useEffect(() => {
    // Initialize socket connection
    const socket = io(process.env.REACT_APP_API_URL || 'http://localhost:4000', {
      transports: ['websocket'],
      timeout: 20000,
    });

    socketRef.current = socket;

    // Connection event handlers
    socket.on('connect', () => {
      console.log('📊 Connected to leadership dashboard WebSocket');
      setIsConnected(true);
      setConnectionError(null);
    });

    socket.on('disconnect', () => {
      console.log('📊 Disconnected from leadership dashboard WebSocket');
      setIsConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('📊 Leadership dashboard WebSocket connection error:', error);
      setConnectionError(error.message);
      setIsConnected(false);
    });

    // Cleanup on unmount
    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  const subscribeToDashboard = (callback: (data: LeadershipWebSocketData) => void) => {
    if (!socketRef.current) return;

    const socket = socketRef.current;
    
    // Subscribe to dashboard updates
    socket.emit('subscribe-leadership-dashboard');
    
    // Listen for updates
    socket.on('dashboard-update', (data: LeadershipWebSocketData) => {
      console.log('📊 Received dashboard update:', data);
      setLastUpdate(new Date());
      callback(data);
    });

    socket.on('dashboard-error', (error: LeadershipWebSocketError) => {
      console.error('📊 Dashboard update error:', error);
      setConnectionError(error.message);
    });

    return () => {
      socket.off('dashboard-update');
      socket.off('dashboard-error');
    };
  };

  const subscribeToUserPerformance = (userId: string, callback: (data: LeadershipWebSocketData) => void) => {
    if (!socketRef.current) return;

    const socket = socketRef.current;
    
    // Subscribe to user performance updates
    socket.emit('subscribe-user-performance', userId);
    
    // Listen for updates
    socket.on('user-performance-update', (data: LeadershipWebSocketData) => {
      if (data.userId === userId) {
        console.log(`👤 Received user performance update for ${userId}:`, data);
        setLastUpdate(new Date());
        callback(data);
      }
    });

    socket.on('user-performance-error', (error: LeadershipWebSocketError) => {
      if (error.userId === userId) {
        console.error(`👤 User performance update error for ${userId}:`, error);
        setConnectionError(error.message);
      }
    });

    return () => {
      socket.off('user-performance-update');
      socket.off('user-performance-error');
    };
  };

  const subscribeToSprintPerformance = (sprintId: string, callback: (data: LeadershipWebSocketData) => void) => {
    if (!socketRef.current) return;

    const socket = socketRef.current;
    
    // Subscribe to sprint performance updates
    socket.emit('subscribe-sprint-performance', sprintId);
    
    // Listen for updates
    socket.on('sprint-performance-update', (data: LeadershipWebSocketData) => {
      if (data.sprintId === sprintId) {
        console.log(`🏃 Received sprint performance update for ${sprintId}:`, data);
        setLastUpdate(new Date());
        callback(data);
      }
    });

    socket.on('sprint-performance-error', (error: LeadershipWebSocketError) => {
      if (error.sprintId === sprintId) {
        console.error(`🏃 Sprint performance update error for ${sprintId}:`, error);
        setConnectionError(error.message);
      }
    });

    return () => {
      socket.off('sprint-performance-update');
      socket.off('sprint-performance-error');
    };
  };

  const subscribeToProjectPerformance = (projectId: string, callback: (data: LeadershipWebSocketData) => void) => {
    if (!socketRef.current) return;

    const socket = socketRef.current;
    
    // Subscribe to project performance updates
    socket.emit('subscribe-project-performance', projectId);
    
    // Listen for updates
    socket.on('project-performance-update', (data: LeadershipWebSocketData) => {
      if (data.projectId === projectId) {
        console.log(`📁 Received project performance update for ${projectId}:`, data);
        setLastUpdate(new Date());
        callback(data);
      }
    });

    socket.on('project-performance-error', (error: LeadershipWebSocketError) => {
      if (error.projectId === projectId) {
        console.error(`📁 Project performance update error for ${projectId}:`, error);
        setConnectionError(error.message);
      }
    });

    return () => {
      socket.off('project-performance-update');
      socket.off('project-performance-error');
    };
  };

  const refreshDashboard = () => {
    if (!socketRef.current) return;
    socketRef.current.emit('refresh-dashboard');
  };

  const disconnect = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    }
  };

  return {
    isConnected,
    lastUpdate,
    connectionError,
    subscribeToDashboard,
    subscribeToUserPerformance,
    subscribeToSprintPerformance,
    subscribeToProjectPerformance,
    refreshDashboard,
    disconnect
  };
};
